/** The entry grid's columns as text, for copying a selection out to a spreadsheet.
 *
 *  Its own file, with no imports, for the reason `NewTasks.rows.ts` gives - and with
 *  structural local types rather than the components' own, so this runs under vitest's
 *  node environment.
 *
 *  ## Flatten, never quote
 *
 *  A tab or a newline inside a value is replaced with a space rather than escaped. It
 *  makes the serialiser total: no quoting rules, no doubled quotes, and nothing that
 *  can be pasted back differently from how it left. Excel's own TSV quote handling is
 *  inconsistent between versions and platforms, so a quoted field is not the safer
 *  choice it looks like.
 *
 *  It costs nothing real here either: `TaskCell` states that the stored task value
 *  stays flat and that its wrapping is presentation, so a newline in the Task column
 *  only ever arrives from a paste.
 */
export type ClipRow = {
  task: string
  company: string
  responsible_ids: string[]
  approver_ids: string[]
  responsible_rule: string
  approver_rule: string
  priority: string
  documents: { name: string; required?: boolean }[]
  /** Reference documents to hand the doer, held in memory until the tasks exist.
   *  Optional so the copy/paste tests can build a row without any. */
  reference?: { name: string; size: number }[]
  date: string
  end_date: string
}

export type ClipContext = {
  /** The day at the top. A row with no date of its own is filed here. */
  day: string
  /** For turning ids back into names. */
  staff: { user_id: string; name: string }[]
}

export type ClipRect = { r0: number; c0: number; r1: number; c1: number }

/** One value, made safe for a tab-separated field. */
export function flat(value: string): string {
  return value.replace(/\s+/g, ' ').trim()
}

/** Everybody on a cell, by name, and the rule if it changes what the names mean.
 *
 *  Every name, not `peopleLabel`'s summary. That returns "Aarti" plus "+2" for a
 *  176px cell; pasting "Aarti +2" into a spreadsheet would be a lie about who the
 *  work is on.
 *
 *  An unknown id falls back to the id rather than being dropped, as `peopleLabel`
 *  does - so a copy can never silently lose a person.
 *
 *  ALL is marked and ANY is silent, because ANY is the default and a column reading
 *  "(any)" on every row says nothing. Below two names neither is said at all: with
 *  one person the two rules are the same sentence.
 */
export function people(ids: string[], rule: string,
                       staff: ClipContext['staff']): string {
  if (!ids.length) return ''
  const names = ids.map(
    (id) => staff.find((p) => p.user_id === id)?.name ?? id)
  const all = ids.length > 1 && rule === 'ALL' ? ' (all)' : ''
  return names.join(', ') + all
}

/** The documents a task must arrive with, by name.
 *
 *  Names rather than `documentsLabel`'s "2 docs", which is a cell summary and not
 *  data. Semicolons rather than commas, because a document name may contain a comma
 *  and "Bank statement, March" would otherwise read as two.
 *
 *  Blank rows are dropped: `DocumentsField` keeps a spare empty row for typing into,
 *  and that is not a document.
 */
export function documents(docs: ClipRow['documents']): string {
  return docs.filter((d) => d.name.trim())
    .map((d) => flat(d.name) + (d.required ? '*' : ''))
    .join('; ')
}

/** The nine columns, in the order the grid shows them.
 *
 *  One table, so the header and the value for a column cannot be written in two
 *  places and disagree. `index` is the row number column, which only appears in a
 *  copy when the selection actually reaches column 0.
 */
export const GRID_COLUMNS: {
  key: string
  header: string
  text: (row: ClipRow, ctx: ClipContext, i: number) => string
}[] = [
  { key: 'index', header: '#', text: (_r, _c, i) => String(i + 1) },
  { key: 'task', header: 'Task', text: (r) => flat(r.task) },
  // The empty string, not the em dash the cell shows: a dash would arrive in a
  // spreadsheet as a value, and "needs a company" is what a blank means.
  { key: 'company', header: 'Company', text: (r) => flat(r.company) },
  { key: 'responsible', header: 'Who does it',
    text: (r, c) => people(r.responsible_ids, r.responsible_rule, c.staff) },
  { key: 'approver', header: 'Who approves',
    text: (r, c) => people(r.approver_ids, r.approver_rule, c.staff) },
  // ---- line 1 ends here: Date is one of the five fields always filled ----
  //
  // The EFFECTIVE date, which is what the column shows and what will happen. A row
  // on the default follows the day at the top, and copying a blank would lose that.
  { key: 'date', header: 'Date', text: (r, c) => r.date || c.day },

  // ---- line 2: the quiet strip most rows never touch ----
  //
  // The ORDER here is the reading order, and it has to match what is on screen or
  // Tab zigzags between the two lines. So Date sits above, with the fields it is
  // filled alongside, and these four follow it.
  { key: 'priority', header: 'Priority', text: (r) => r.priority },
  { key: 'documents', header: 'Documents', text: (r) => documents(r.documents) },
  // Next to Documents, and a different thing from it: Documents lists what the task
  // must arrive WITH, this is what the doer is being given to work FROM.
  //
  // Filenames only - no text could put a file back, which is why `acceptCell`
  // refuses to try. Semicolons for the reason `documents` gives: a filename may well
  // contain a comma, and splitting on it would turn one file into two.
  { key: 'reference', header: 'Reference',
    text: (r) => (r.reference ?? []).map((f) => flat(f.name)).join('; ') },
  // Blank stays blank: an ordinary one-day task has no end, and repeating the start
  // date here would invent a window the row does not have.
  { key: 'end_date', header: 'Until', text: (r) => r.end_date || '' },
]

/** A rectangle of the grid as tab-separated text.
 *
 *  Rows joined with newlines and fields with tabs, which is what a spreadsheet reads
 *  from the clipboard. No header row: a selection is a rectangle somebody chose, and
 *  prepending headers to three cells from the middle of the grid would paste
 *  something they did not select.
 */
export function tsv(rows: ClipRow[], rect: ClipRect, ctx: ClipContext): string {
  const out: string[] = []
  for (let r = rect.r0; r <= rect.r1 && r < rows.length; r++) {
    const line: string[] = []
    for (let c = rect.c0; c <= rect.c1 && c < GRID_COLUMNS.length; c++) {
      line.push(GRID_COLUMNS[c].text(rows[r], ctx, r))
    }
    out.push(line.join('\t'))
  }
  return out.join('\n')
}

// ---------------------------------------------------------------- coming back in
/** What a column needs to turn text back into row fields. */
export type PasteContext = ClipContext & {
  companies: { name: string }[]
}

/** A pasted block, split into cells.
 *
 *  The inverse of `tsv`, and deliberately as forgiving: Excel writes `\r\n` on
 *  Windows and `\n` elsewhere, and a trailing newline on the last row is normal
 *  rather than an empty row somebody meant.
 */
export function parseTsv(text: string): string[][] {
  return text.replace(/\r\n?/g, '\n').replace(/\n+$/, '')
    .split('\n').map((line) => line.split('\t'))
}

/** A date typed or pasted, as ISO, or '' if it will not read.
 *
 *  Day-first, matching the server's own rule and for the same reason: this is an
 *  Indian firm, and 03/04/2026 quietly becoming 4 March moves a filing deadline by a
 *  month with nothing on screen to show it.
 */
export function readDate(text: string): string {
  const said = text.trim()
  if (!said) return ''
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(said)
  if (iso) return said
  // d/m/yyyy and d-m-yyyy, day first.
  const dmy = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(said)
  if (dmy) {
    const [, d, m, y] = dmy
    if (+m >= 1 && +m <= 12 && +d >= 1 && +d <= 31) {
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
    }
    return ''
  }
  const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun',
                  'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
  const dmn = /^(\d{1,2})[ -]([A-Za-z]{3,})[ -](\d{4})$/.exec(said)
  if (dmn) {
    const m = MONTHS.indexOf(dmn[2].slice(0, 3).toLowerCase())
    if (m >= 0) {
      return `${dmn[3]}-${String(m + 1).padStart(2, '0')}-${dmn[1].padStart(2, '0')}`
    }
  }
  return ''
}

/** One pasted cell as row fields, plus what could not be resolved.
 *
 *  Names are matched against the lists already loaded for the pickers, so a paste
 *  cannot put an id in a cell that the picker could not then name. Anything
 *  unmatched leaves the field alone and reports itself, which is the same contract
 *  the sheet import has - nothing is silently dropped, and nothing is guessed.
 */
export function acceptCell(col: string, text: string, ctx: PasteContext):
    { patch: Partial<ClipRow>; problems: { field: string; value: string;
                                           message: string }[] } {
  const said = text.trim()
  const problems: { field: string; value: string; message: string }[] = []
  const person = (raw: string, field: string) => {
    // The "(all)" a copy appends is the rule, not part of anybody's name.
    const all = / \(all\)$/.test(raw)
    const wanted = raw.replace(/ \(all\)$/, '')
      .split(',').map((n) => n.trim()).filter(Boolean)
    const ids: string[] = []
    for (const name of wanted) {
      const hit = ctx.staff.filter((p) => p.name.toLowerCase() === name.toLowerCase())
      if (hit.length === 1) ids.push(hit[0].user_id)
      else if (hit.length > 1) {
        problems.push({ field, value: name,
                        message: `More than one person is called “${name}”.` })
      } else {
        problems.push({ field, value: name,
                        message: `“${name}” is not somebody you can put on a task.` })
      }
    }
    return { ids, rule: (all ? 'ALL' : 'ANY') }
  }

  switch (col) {
    case 'task':
      return { patch: { task: said.slice(0, 2000) }, problems }
    case 'company': {
      if (!said) return { patch: { company: '' }, problems }
      const hit = ctx.companies.find(
        (c) => c.name.toLowerCase() === said.toLowerCase())
      if (hit) return { patch: { company: hit.name }, problems }
      problems.push({ field: 'company', value: said,
                      message: `No company called “${said}”.` })
      return { patch: {}, problems }
    }
    case 'responsible': {
      const { ids, rule } = person(said, 'responsible')
      return { patch: { responsible_ids: ids, responsible_rule: rule }, problems }
    }
    case 'approver': {
      const { ids, rule } = person(said, 'approver')
      return { patch: { approver_ids: ids, approver_rule: rule }, problems }
    }
    case 'priority': {
      if (!said) return { patch: {}, problems }
      const want = said.charAt(0).toUpperCase() + said.slice(1).toLowerCase()
      if (['Urgent', 'Important', 'Regular'].includes(want)) {
        return { patch: { priority: want }, problems }
      }
      problems.push({ field: 'priority', value: said,
                      message: `“${said}” is not a priority.` })
      return { patch: {}, problems }
    }
    case 'documents':
      return {
        patch: {
          documents: said.split(';').map((d) => d.trim()).filter(Boolean)
            .map((d) => ({ name: d.replace(/\*$/, '').trim(),
                           required: d.endsWith('*') })),
        }, problems }
    case 'date':
    case 'end_date': {
      if (!said) return { patch: { [col]: '' } as Partial<ClipRow>, problems }
      const iso = readDate(said)
      if (!iso) {
        problems.push({ field: col, value: said,
                        message: `“${said}” is not a date I can read.` })
        return { patch: {}, problems }
      }
      return { patch: { [col]: iso } as Partial<ClipRow>, problems }
    }
    // A file cannot come out of a spreadsheet or off the clipboard, so a pasted
    // Reference column is ignored rather than half-applied. Said out loud because
    // silently dropping a column of a pasted block is the kind of thing somebody
    // should be able to find the reason for.
    case 'reference':
      return { patch: {}, problems }
    // The row number is a label, not a field - pasting over it changes nothing.
    default:
      return { patch: {}, problems }
  }
}

/** Blank a rectangle of cells.
 *
 *  Delete over a selection is the gesture every spreadsheet has, and without it the
 *  only way to undo a bad paste is to retype the cells one at a time.
 *
 *  `patch` per column rather than a wholesale reset, because "empty" differs by
 *  column: the two rule fields go back to ANY rather than to '', and Priority to its
 *  default rather than to nothing the select can show.
 */
export function blankCell(col: string): Partial<ClipRow> {
  switch (col) {
    case 'task': return { task: '' }
    case 'company': return { company: '' }
    case 'responsible': return { responsible_ids: [], responsible_rule: 'ANY' }
    case 'approver': return { approver_ids: [], approver_rule: 'ANY' }
    case 'priority': return { priority: 'Regular' }
    case 'documents': return { documents: [] }
    case 'reference': return { reference: [] }
    case 'date': return { date: '' }
    case 'end_date': return { end_date: '' }
    default: return {}
  }
}

/** What one row holds in one column, as a patch - for filling down.
 *
 *  Copying the VALUES rather than re-parsing the text: a fill-down from a cell whose
 *  approver could not be named would otherwise lose them on the way, and the point
 *  of the gesture is that the row below ends up the same as the row above.
 */
export function copyCell(from: ClipRow, col: string): Partial<ClipRow> {
  switch (col) {
    case 'task': return { task: from.task }
    case 'company': return { company: from.company }
    case 'responsible': return { responsible_ids: [...from.responsible_ids],
                                 responsible_rule: from.responsible_rule }
    case 'approver': return { approver_ids: [...from.approver_ids],
                              approver_rule: from.approver_rule }
    case 'priority': return { priority: from.priority }
    case 'documents': return { documents: from.documents.map((d) => ({ ...d })) }
    // A new ARRAY of the same File objects. The files themselves are immutable and
    // the upload reads each once, so sharing them is right - but sharing the array
    // would mean adding a file to one row silently added it to every row the
    // fill-down had touched.
    case 'reference': return { reference: [...(from.reference ?? [])] }
    case 'date': return { date: from.date }
    case 'end_date': return { end_date: from.end_date }
    default: return {}
  }
}
